import React, { type ReactNode } from 'react'
import { colors } from '@retailer-platform/shared-components'
import { AiButtonFloating } from '../../../gin-and-tonic/common/ai-button-floating/AiButtonFloating'
import { useAiAgent } from './AiAgentContext'
import { AiAgentChatBar } from './AiAgentChatBar'

interface AiPageLayoutProps {
  children: ReactNode
}

/**
 * A layout wrapper that provides responsive width adjustment for the AI chat panel.
 * When the AI chat is open, this component automatically adjusts the content width
 * to make room for the 325px chat sidebar.
 *
 * Usage:
 * <AiPageLayout>
 *   <YourPageContent />
 * </AiPageLayout>
 */
export const AiPageLayout: React.FC<AiPageLayoutProps> = ({ children }) => {
  const { isChatOpen, isAgentPowered } = useAiAgent()

  return (
    <>
      {isAgentPowered && !isChatOpen && <AiButtonFloating />}
      <div
        css={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflowY: 'hidden',
          width: isChatOpen ? 'calc(100% - 325px)' : '100%',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div css={{ flex: 1, minWidth: 0 }}>{children}</div>

        {isAgentPowered && (
          <div
            css={{
              width: isChatOpen ? '325px' : '0px',
              flexShrink: 0,
              borderLeft: isChatOpen ? `1px solid ${colors.GRAYSCALE.X20}` : 'none',
              backgroundColor: colors.GRAYSCALE.X0,
              overflow: 'hidden',
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <div css={{ width: '325px', height: '100%' }}>
              <AiAgentChatBar />
            </div>
          </div>
        )}
      </div>
    </>
  )
}
